import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Box,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import * as g04GeneralDocuActions from '../../store/g04GeneralDocu';
import * as g04docuGCertificationActions from '../../store/g04docuGCertification';
import * as gstandardActions from '../../store/gstandard';
import * as gtestComparisonActions from '../../store/gtestComparison';
import * as subMatFolderActions from '../../store/subMatFolder';
import * as gmaterialSlotActions from '../../store/gmaterialSlot';
import { ConfirmDialog } from '../dialog';
import { collectLeadKeys } from '../GTestComparison';
import SubMatFolderGlassBom from './SubMatFolderGlassBom';
import SubMatFolderMaterialSlotFields from './SubMatFolderMaterialSlotFields';
import SubMatFolderClassification, { sumProcessThickness } from './SubMatFolderClassification';
import { GlassUtils } from '../../utils';

/** 일반문서·인증서·성적서. 루트에는 속성을 두지 않는다. */
export const folderAcceptsSpec = (folder) => Boolean(
  folder
  && folder.folderLevel > 0
  && (folder.rootKind === 'GENERAL' || folder.rootKind === 'TEST' || folder.rootKind === 'CERTIFICATION'),
);

const DIVISION_CHIPS = [
  { value: 'RAW_MATERIAL', label: '원판' },
  { value: 'SUB_MATERIAL_PROCESS', label: '가공부자재' },
  { value: 'SUB_MATERIAL_BUILD', label: '시공부자재' },
  { value: 'PROCESS', label: '가공' },
];

/** 폴더명 끝의 회사 괄호를 뺀 값이 제품명이다. CL(KCC) → CL */
export const productNameFromFolderName = (name) => String(name || '').replace(/\s*\([^)]*\)\s*$/, '').trim();

const CERT_DIVISIONS = DIVISION_CHIPS.map((chip) => chip.value);

/** 훅보다 앞에서 분기하면 이 프로젝트의 ESLint가 뒤 훅을 조건부 호출로 본다. */
const inheritedTestContext = (folder, folders) => {
  const rootKind = folder && folder.rootKind;
  const inheritedProduct = rootKind === 'TEST'
    ? subMatFolderActions.ancestorNamedProduct(folder, folders)
    : null;
  const spec = inheritedProduct && inheritedProduct.spec;
  return {
    generalFolder: rootKind === 'GENERAL',
    certificateFolder: rootKind === 'CERTIFICATION',
    inheritedProduct,
    inheritedCertId: (spec && spec.g04docuGCertificationId) || '',
    inheritedStandardId: (spec && spec.gstandardId) || '',
  };
};

const passesIncludeTest = (cert) => cert?.division !== 'PROCESS' || cert?.includeTest === true;

const CertOptionLabel = ({ cert }) => (
  <>
    {cert?.gstandardNo ? <Chip label={cert.gstandardNo} size="small" sx={{ mr: 1 }} /> : null}
    {cert?.name}
  </>
);

/**
 * 부자재 세분류는 구분 전체, 가공 세분류는 그 행에 연결된 인증규격만.
 * 가공은 세분류 code(강화·복층 등) 또는 g04docuGCertificationId로 맞춘다.
 */
const companyCertFilter = (types) => {
  const certIds = new Set();
  const codes = new Set();
  const subMatDivisions = new Set();
  (types || []).forEach((type) => {
    if (type?.code === 'MANUFACTURER_RAW_MATERIAL') subMatDivisions.add('RAW_MATERIAL');
    if (type?.code === 'MANUFACTURER_SUB_MATERIAL_PROCESS') subMatDivisions.add('SUB_MATERIAL_PROCESS');
    if (type?.code === 'MANUFACTURER_SUB_MATERIAL_BUILD') subMatDivisions.add('SUB_MATERIAL_BUILD');
    if (type?.code) codes.add(type.code);
    const certId = type?.g04docuGCertificationId || type?.g04docuGCertification?.id;
    if (certId) certIds.add(certId);
  });
  return { certIds, codes, subMatDivisions };
};

const certAllowed = (cert, filter, forCertificate) => {
  if (!forCertificate && !passesIncludeTest(cert)) return false;
  if (cert.division === 'PROCESS') {
    return filter.certIds.has(cert.id) || (cert.code && filter.codes.has(cert.code));
  }
  return filter.subMatDivisions.has(cert.division);
};

const SubMatFolderSpecDialog = ({
  open,
  folder,
  folders = [],
  linkTargetNames = [],
  onOpenLinks,
  onClose,
  onSaved,
  onError,
  /** 보고 있는 회사의 세분류. null이면 아직 조회 중. */
  companyTypes,
}) => {
  const dispatch = useDispatch();
  const [certs, setCerts] = useState([]);
  const [certsReady, setCertsReady] = useState(false);
  const [chipDivisions, setChipDivisions] = useState([]);
  const [division, setDivision] = useState(DIVISION_CHIPS[0].value);
  const [certId, setCertId] = useState('');
  const [standardDetail, setStandardDetail] = useState(null);
  const [leadKeys, setLeadKeys] = useState([]);
  const [detailReady, setDetailReady] = useState(false);
  const [parts, setParts] = useState([]);
  const [reportScope, setReportScope] = useState('common');
  const [thickness, setThickness] = useState('');
  const [materialSlots, setMaterialSlots] = useState([]);
  const [slotValues, setSlotValues] = useState([]);
  const [glass, setGlass] = useState(null);
  const [saving, setSaving] = useState(false);
  const [clearOpen, setClearOpen] = useState(false);
  const [generalDocs, setGeneralDocs] = useState([]);
  const [generalDocId, setGeneralDocId] = useState('');
  const [conflict, setConflict] = useState(null);
  const [pendingSpec, setPendingSpec] = useState(null);

  const {
    generalFolder,
    certificateFolder,
    inheritedProduct,
    inheritedCertId,
    inheritedStandardId,
  } = inheritedTestContext(folder, folders);

  useEffect(() => {
    if (!open || folder?.rootKind === 'GENERAL') return undefined;
    if (companyTypes === null) {
      setCerts([]);
      setChipDivisions([]);
      setCertsReady(false);
      return undefined;
    }
    let cancelled = false;
    setCertsReady(false);
    (async () => {
      try {
        const lists = await Promise.all(CERT_DIVISIONS.map((division) => (
          g04docuGCertificationActions.selectByDivisionByQueryDirect(division)
        )));
        const filter = companyCertFilter(companyTypes);
        const merged = [];
        lists.flat().forEach((cert) => {
          if (!cert?.id || !CERT_DIVISIONS.includes(cert.division) || !certAllowed(cert, filter, folder?.rootKind === 'CERTIFICATION')) return;
          if (merged.some((item) => item.id === cert.id)) return;
          merged.push(cert);
        });
        const ordered = CERT_DIVISIONS.filter((item) => merged.some((cert) => cert.division === item));
        if (!cancelled) {
          setChipDivisions(ordered);
          setCerts(merged);
        }
      } catch (err) {
        if (!cancelled) {
          setCerts([]);
          onError?.(err?.serverResponse?.message || err.message || '인증규격을 불러오지 못했습니다.');
        }
      } finally {
        if (!cancelled) setCertsReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, [open, companyTypes, folder?.rootKind]);

  useEffect(() => {
    if (!open || generalFolder || certificateFolder) {
      setMaterialSlots([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const slots = await dispatch(gmaterialSlotActions.selectAll());
        if (!cancelled) setMaterialSlots(slots || []);
      } catch (_err) {
        if (!cancelled) setMaterialSlots([]);
      }
    })();
    return () => { cancelled = true; };
  }, [open, generalFolder, certificateFolder, dispatch]);

  useEffect(() => {
    if (!open || !generalFolder) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const list = await g04GeneralDocuActions.selectAllDirect();
        if (!cancelled) {
          setGeneralDocs([...list].sort((a, b) => (a.order || 0) - (b.order || 0) || (a.name || '').localeCompare(b.name || '', 'ko')));
        }
      } catch (err) {
        if (!cancelled) {
          setGeneralDocs([]);
          onError?.(err?.serverResponse?.message || err.message || '일반문서 종류를 불러오지 못했습니다.');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [open, generalFolder]);

  useEffect(() => {
    if (!open) {
      setCertId('');
      setParts([]);
      setReportScope('common');
      setThickness('');
      setSlotValues([]);
      setGlass(null);
      setGeneralDocId('');
      setConflict(null);
      setPendingSpec(null);
      return;
    }
    const spec = folder?.spec || {};
    setGeneralDocId(spec.g04GeneralDocuId || '');
    const savedId = inheritedCertId || spec.g04docuGCertificationId || '';
    const known = certs.find((cert) => cert.id === savedId);
    setCertId(savedId && (inheritedCertId || known) ? savedId : '');
    if (known?.division) setDivision(known.division);
    const savedParts = Array.isArray(spec.classificationParts) ? [...spec.classificationParts] : [];
    setParts((known || inheritedCertId) && savedParts.length ? savedParts : []);
    setReportScope(savedParts.length ? 'grade' : 'common');
    setThickness(known && spec.thickness != null ? String(spec.thickness) : '');
    const savedThickness = spec.thickness != null && String(spec.thickness) !== ''
      ? [{ code: String(spec.thickness) }]
      : [];
    setSlotValues(
      known && Array.isArray(spec.materialSlotValues) && spec.materialSlotValues.length
        ? spec.materialSlotValues
        : ((known || inheritedCertId) && (spec.productFolderId || inheritedCertId) ? savedThickness : []),
    );
    setGlass(known?.division === 'PROCESS' && spec.glass?.gglassId ? spec.glass : null);
  }, [open, folder?.id, folder?.spec, certs, inheritedCertId]);

  useEffect(() => {
    if (!chipDivisions.length || chipDivisions.includes(division)) return;
    setDivision(chipDivisions[0]);
  }, [chipDivisions, division]);

  const visibleCerts = useMemo(
    () => certs.filter((cert) => cert.division === division),
    [certs, division],
  );
  const selected = certs.find((cert) => cert.id === certId) || null;
  const standardId = selected?.gstandardId || '';

  useEffect(() => {
    if (!open || generalFolder || certificateFolder || !standardId) {
      setStandardDetail(null);
      setLeadKeys([]);
      setDetailReady(!open || generalFolder || certificateFolder || !certId);
      return undefined;
    }
    setDetailReady(false);
    let cancelled = false;
    (async () => {
      try {
        const [detail, comparisons] = await Promise.all([
          gstandardActions.selectDirect(standardId),
          gtestComparisonActions.selectAllByGstandardIdDirect(standardId, { evaluation: true }),
        ]);
        if (cancelled) return;
        setStandardDetail(detail);
        setLeadKeys(collectLeadKeys(comparisons));
        setDetailReady(true);
      } catch (err) {
        if (!cancelled) {
          onError?.(err?.serverResponse?.message || err.message || '표준 정보를 불러오지 못했습니다.');
          setLeadKeys([]);
          setDetailReady(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [open, standardId, certId, generalFolder, certificateFolder]);

  const isProcess = !certificateFolder && !inheritedProduct && selected?.division === 'PROCESS';
  const slotCertId = inheritedProduct ? inheritedCertId : certId;
  const materialSlot = !certificateFolder && !isProcess
    ? materialSlots.find((slot) => (slot.certificationIds || (slot.certifications || []).map((cert) => cert.g04docuGCertificationId)).includes(slotCertId))
    : null;
  const savedProduct = Boolean(folder?.spec?.productName);
  const savedReport = Boolean(folder?.spec?.productFolderId);
  const legacySelfGrade = !inheritedProduct && !savedProduct && !savedReport
    && Array.isArray(folder?.spec?.classificationParts)
    && folder.spec.classificationParts.length > 0;
  const gradeReportMode = Boolean(inheritedProduct) && !materialSlot;
  const openProductMode = !generalFolder && !certificateFolder && !inheritedProduct && !materialSlot
    && !isProcess && !legacySelfGrade && !savedReport;
  const productMode = !inheritedProduct && Boolean(materialSlot) && (savedProduct || (!inheritedProduct && !savedReport));
  const reportMode = Boolean(materialSlot) && (Boolean(inheritedProduct) || (!productMode && savedReport));
  const productName = productNameFromFolderName(folder?.name);
  const reportProductName = inheritedProduct?.spec?.productName || '';
  const reportProductFolderId = inheritedProduct?.id || folder?.spec?.productFolderId || '';
  const thicknessDetails = (materialSlot?.details || []).filter((detail) => {
    const code = String(detail.gcomponentCode || '');
    const name = String(detail.gcomponentName || '');
    return code.includes('THICKNESS') || name.includes('두께');
  });
  const thicknessValue = () => {
    const row = thicknessDetails
      .map((detail) => (
        slotValues.find((item) => item.gcomponentId === detail.gcomponentId)
        || slotValues.find((item) => item.code && !item.gcomponentId)
      ))
      .find((item) => item?.code);
    return row?.code || '';
  };
  const hasChildren = (folders || []).some((row) => row.parentId === folder?.id);
  const slotComplete = !reportMode || Boolean(thicknessValue());
  const slotGradeCert = reportMode ? (selected || certs.find((cert) => cert.id === inheritedCertId) || null) : null;
  const slotGradeRows = Array.isArray(slotGradeCert?.classifications) ? slotGradeCert.classifications : [];
  const slotGradeRequired = reportMode && slotGradeRows.length > 0;
  const showClassification = !gradeReportMode && !openProductMode && !inheritedProduct && !certificateFolder && !materialSlot && (leadKeys.includes('classification') || leadKeys.includes('ft_thick_class'));
  const showThickness = !openProductMode && !inheritedProduct && !certificateFolder && !isProcess && !materialSlot && (leadKeys.includes('thickness') || leadKeys.includes('ft_thick_class'));
  const canSave = !saving && (
    generalFolder
      ? Boolean(generalDocId)
      : gradeReportMode
        ? Boolean(inheritedCertId) && Boolean(reportProductFolderId) && (reportScope === 'common' || parts.length > 0)
        : inheritedProduct
          ? Boolean(inheritedCertId) && Boolean(materialSlot) && slotComplete && Boolean(reportProductFolderId) && (!slotGradeRequired || parts.length > 0)
          : certificateFolder
            ? Boolean(certId) && Boolean(standardId) && certsReady
            : openProductMode
              ? Boolean(certId) && Boolean(standardId) && Boolean(productName)
              : productMode
                ? Boolean(certId) && Boolean(standardId) && Boolean(productName)
                : Boolean(certId) && Boolean(standardId) && detailReady && (!isProcess || (Boolean(glass?.gglassId) && !hasChildren)) && slotComplete && (!reportMode || Boolean(reportProductFolderId)) && (!slotGradeRequired || parts.length > 0)
  );

  const handleCertChange = (nextId) => {
    setCertId(nextId);
    setParts([]);
    setThickness('');
    setSlotValues([]);
    setGlass(null);
  };

  const handleDivisionChange = (_event, next) => {
    if (!next || next === division) return;
    setDivision(next);
    if (!certs.some((cert) => cert.id === certId && cert.division === next)) {
      handleCertChange('');
    }
  };

  const buildSpec = () => {
    if (generalFolder) return { g04GeneralDocuId: generalDocId };
    if (gradeReportMode) {
      return {
        g04docuGCertificationId: inheritedCertId,
        gstandardId: inheritedStandardId,
        productFolderId: inheritedProduct.id,
        classificationParts: reportScope === 'common' ? [] : parts,
      };
    }
    if (inheritedProduct) {
      return {
        g04docuGCertificationId: inheritedCertId,
        gstandardId: inheritedStandardId,
        thickness: thicknessValue(),
        productFolderId: inheritedProduct.id,
        ...(slotGradeRequired ? { classificationParts: parts } : {}),
      };
    }
    if (!selected) return null;
    if (certificateFolder) {
      return {
        g04docuGCertificationId: selected.id,
        gstandardId: selected.gstandardId,
      };
    }
    const [specification, , specificationByGStandard] = isProcess
      ? GlassUtils.getSpecification(glass?.selectedGcomponentItems || [])
      : ['', '', ''];
    if (openProductMode || productMode) {
      return {
        g04docuGCertificationId: selected.id,
        gstandardId: selected.gstandardId,
        productName,
      };
    }
    if (reportMode) {
      return {
        g04docuGCertificationId: selected.id,
        gstandardId: selected.gstandardId,
        thickness: thicknessValue(),
        productFolderId: reportProductFolderId,
        ...(slotGradeRequired ? { classificationParts: parts } : {}),
      };
    }
    return {
      g04docuGCertificationId: selected.id,
      gstandardId: selected.gstandardId,
      classificationParts: showClassification ? parts : [],
      thickness: isProcess
        ? sumProcessThickness(glass?.selectedGcomponentItems, standardDetail?.no || selected?.gstandardNo)
        : (showThickness ? thickness : ''),
      ...(isProcess ? {
        glass,
        code: specification || '',
        codeByStandard: specificationByGStandard || '',
      } : {}),
    };
  };

  const persistSpec = async (spec, replaceFolderId) => {
    if (!folder?.id || !spec) return;
    setSaving(true);
    try {
      const saved = await dispatch(subMatFolderActions.saveFolderSpec(folder.id, spec, replaceFolderId));
      setConflict(null);
      setPendingSpec(null);
      onSaved?.(saved);
      onClose?.();
    } catch (err) {
      const details = err?.serverResponse?.details;
      if (details?.conflict && details.folderId) {
        setPendingSpec(spec);
        setConflict(details);
        return;
      }
      onError?.(err?.serverResponse?.message || err.message || '속성을 저장하지 못했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => {
    if (gradeReportMode && inheritedProduct?.currentFile) {
      onError?.(subMatFolderActions.PRODUCT_FILE_BLOCKS_SPLIT);
      return;
    }
    persistSpec(buildSpec());
  };

  const handleMoveAttribute = () => {
    if (!pendingSpec || !conflict?.folderId) return;
    persistSpec(pendingSpec, conflict.folderId);
  };

  const handleClear = async () => {
    if (!folder?.id) return;
    setSaving(true);
    try {
      const saved = await dispatch(subMatFolderActions.saveFolderSpec(folder.id, null));
      setCertId('');
      setParts([]);
      setThickness('');
      setSlotValues([]);
      setGlass(null);
      setGeneralDocId('');
      onSaved?.(saved);
      onClose?.();
    } catch (err) {
      onError?.(err?.serverResponse?.message || err.message || '속성을 지우지 못했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const selectValue = visibleCerts.some((cert) => cert.id === certId) ? certId : '';

  return (
    <>
    <Dialog
      open={open}
      onClose={saving ? undefined : () => { setClearOpen(false); onClose?.(); }}
      fullWidth={generalFolder || certificateFolder}
      maxWidth={generalFolder || certificateFolder ? 'sm' : false}
      PaperProps={generalFolder || certificateFolder ? undefined : {
        sx: {
          width: isProcess ? 'min(900px, calc(100% - 64px))' : 'min(980px, calc(100% - 64px))',
          maxWidth: isProcess ? 'min(900px, calc(100% - 64px))' : 'min(980px, calc(100% - 64px))',
        },
      }}
    >
      <DialogTitle>{folder?.name ? `${folder.name} 속성` : '속성'}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0 }}>
          {linkTargetNames.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="body2">
                추가 서류{' '}
                <Button
                  size="small"
                  onClick={() => onOpenLinks?.()}
                  sx={{ p: 0, minWidth: 0, verticalAlign: 'baseline', textTransform: 'none' }}
                >
                  {linkTargetNames.join(', ')}
                </Button>
              </Typography>
            </Grid>
          )}
          {generalFolder && (
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel id="folder-spec-general-label">일반문서 종류</InputLabel>
                <Select
                  labelId="folder-spec-general-label"
                  label="일반문서 종류"
                  value={generalDocs.some((doc) => doc.id === generalDocId) ? generalDocId : ''}
                  onChange={(e) => setGeneralDocId(e.target.value)}
                >
                  {generalDocs.map((doc) => (
                    <MenuItem key={doc.id} value={doc.id}>{doc.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          {!generalFolder && !inheritedProduct && chipDivisions.length > 0 && (
            <Grid item xs={12}>
              <ToggleButtonGroup
                color="primary"
                exclusive
                size="small"
                value={chipDivisions.includes(division) ? division : chipDivisions[0]}
                onChange={handleDivisionChange}
              >
                {DIVISION_CHIPS.filter((chip) => chipDivisions.includes(chip.value)).map((chip) => (
                  <ToggleButton key={chip.value} value={chip.value} sx={{ fontWeight: 'bold' }}>
                    {chip.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Grid>
          )}
          {!generalFolder && !inheritedProduct && (
          <Grid item xs={12}>
            <FormControl fullWidth size="small">
                <InputLabel id="folder-spec-cert-label">{certificateFolder ? '인증규격' : '규격'}</InputLabel>
              <Select
                labelId="folder-spec-cert-label"
                label={certificateFolder ? '인증규격' : '규격'}
                value={selectValue}
                onChange={(e) => handleCertChange(e.target.value)}
                renderValue={(value) => {
                  const cert = visibleCerts.find((item) => item.id === value);
                  if (!cert) return '';
                  return <CertOptionLabel cert={cert} />;
                }}
              >
                {visibleCerts.map((cert) => (
                  <MenuItem key={cert.id} value={cert.id}>
                    <CertOptionLabel cert={cert} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          )}
          {!generalFolder && companyTypes === null && (
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                거래처 세분류를 불러오는 중입니다.
              </Typography>
            </Grid>
          )}
          {!generalFolder && certsReady && certs.length === 0 && (
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                세분류에 연결된 원판·가공·부자재 인증규격이 없습니다.
              </Typography>
            </Grid>
          )}
          {isProcess && hasChildren && (
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                하위 폴더가 있으면 가공 속성을 저장할 수 없습니다. 하위 폴더를 옮긴 뒤 저장하세요.
              </Typography>
            </Grid>
          )}
          {isProcess && certId && (
            <Grid item xs={12}>
              <SubMatFolderGlassBom
                certificationId={certId}
                value={glass}
                onChange={setGlass}
              />
            </Grid>
          )}
          {(productMode || openProductMode) && (
            <Grid item xs={12}>
              <Typography variant="body2">
                {productMode
                  ? `제품명 ${productName || '(폴더 이름이 비어 있습니다)'}. 폴더 이름이 제품명이고, 두께는 아래 성적서 폴더에서 고릅니다.`
                  : `제품명 ${productName || '(폴더 이름이 비어 있습니다)'}. 폴더 이름이 제품명입니다. 공통 서류와 등급 서류는 아래 폴더에서 고릅니다.`}
              </Typography>
            </Grid>
          )}
          {gradeReportMode && (
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {`위 제품 ${reportProductName || ''}의 인증규격을 씁니다. 이 제품 공통이면 그 제품일 때 항상 붙고, 등급을 지정하면 그 등급일 때만 붙습니다.`}
              </Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                color="primary"
                value={reportScope}
                onChange={(_event, next) => {
                  if (!next) return;
                  setReportScope(next);
                  if (next === 'common') setParts([]);
                }}
                sx={{ mb: 2 }}
              >
                <ToggleButton value="common">이 제품 공통</ToggleButton>
                <ToggleButton value="grade">등급 지정</ToggleButton>
              </ToggleButtonGroup>
              {reportScope === 'grade' && selected && (
                Array.isArray(selected.classifications) && selected.classifications.length > 0
                  ? (
                    <SubMatFolderClassification
                      certId={inheritedCertId}
                      classifications={selected.classifications}
                      isProcess={false}
                      certCode={selected.code}
                      savedParts={folder?.spec?.classificationParts}
                      onChange={setParts}
                    />
                  )
                  : (
                    <Typography variant="body2" color="text.secondary">
                      이 인증규격에 등록된 등급이 없습니다.
                    </Typography>
                  )
              )}
            </Grid>
          )}
          {reportMode && (
            <Grid item xs={12}>
              <SubMatFolderMaterialSlotFields
                slot={materialSlot}
                value={slotValues}
                onChange={setSlotValues}
                lockedProductName={reportProductName}
              />
              {slotGradeRequired && (
                <Box sx={{ mt: 2 }}>
                  <SubMatFolderClassification
                    certId={slotGradeCert.id}
                    classifications={slotGradeRows}
                    isProcess={false}
                    certCode={slotGradeCert.code}
                    savedParts={folder?.spec?.classificationParts}
                    onChange={setParts}
                  />
                </Box>
              )}
            </Grid>
          )}
          {!generalFolder && selected && !standardId && (
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                이 인증규격에는 표준이 연결되어 있지 않습니다.
              </Typography>
            </Grid>
          )}
          {showClassification && selected && (
            <Grid item xs={12}>
              <SubMatFolderClassification
                certId={selected.id}
                classifications={selected.classifications}
                isProcess={isProcess}
                certCode={selected.code}
                glassItems={glass?.selectedGcomponentItems}
                savedParts={folder?.spec?.g04docuGCertificationId === selected.id ? folder.spec.classificationParts : []}
                onChange={setParts}
              />
            </Grid>
          )}
          {showThickness && (
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="두께 (mm)"
                value={thickness}
                onChange={(e) => setThickness(e.target.value)}
                inputProps={{ step: 'any' }}
              />
            </Grid>
          )}
          {certificateFolder && standardId && (
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                이 폴더의 현재 파일이 자재승인서의 이 인증서입니다.
              </Typography>
            </Grid>
          )}
          {!generalFolder && !certificateFolder && (inheritedProduct || standardId) && (
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                {gradeReportMode
                  ? '구분 폴더는 속성을 비워 둡니다. 성적서마다 폴더를 만들고 여기서 공통 또는 등급을 고릅니다.'
                  : inheritedProduct
                    ? (slotGradeRequired
                      ? '제품 다음에 규격이 나옵니다. 제품은 위 폴더 값으로 고정되어 있고, 두께와 등급을 고릅니다. 등급이 하나면 그 값이 고정됩니다.'
                      : '제품 다음에 규격이 나옵니다. 제품은 위 폴더 값으로 고정되어 있고, 두께만 고르면 됩니다.')
                    : isProcess
                      ? '표준은 고른 인증규격에 연결된 것을 씁니다. 두께는 유리 구성에서 계산하고, 등급은 조합에서 고릅니다.'
                      : (productMode || openProductMode)
                        ? (productMode
                          ? '이 폴더가 제품입니다. 조회에 쓰는 코드는 아래 성적서 폴더의 두께와 이 제품명을 붙인 두께|제품명입니다.'
                          : '이 폴더가 제품입니다. 아래에는 이름만 있는 구분 폴더와, 공통 또는 등급을 고른 성적서 폴더를 둡니다.')
                        : '표준은 고른 인증규격에 연결된 것을 씁니다. 대비표의 두께_등급은 두께와 등급 1단으로 합성됩니다.'}
              </Typography>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions
        disableSpacing
        sx={{
          // theme.js `.MuiDialogActions-spacing` 은 오른쪽 10px. 내용 영역은 24px라 저장이 규격보다 밖으로 나간다.
          '&&': {
            justifyContent: 'flex-end',
            gap: 1,
            padding: '15px 24px 32px !important',
          },
        }}
      >
        {(folder?.spec?.g04GeneralDocuId || folder?.spec?.g04docuGCertificationId || folder?.spec?.gstandardId) && (
          <Button disabled={saving} onClick={() => setClearOpen(true)}>
            지우기
          </Button>
        )}
        <Button onClick={onClose} disabled={saving}>취소</Button>
        <Button variant="contained" disabled={!canSave} onClick={handleSave}>
          저장
        </Button>
      </DialogActions>
    </Dialog>
    <ConfirmDialog
      removeId={folder?.id}
      title="규격 속성 지우기"
      open={clearOpen}
      setOpen={setClearOpen}
      onConfirm={handleClear}
      onCancel={() => setClearOpen(false)}
    >
      이 폴더의 속성을 지우시겠습니까? 파일은 폴더에 남습니다.
    </ConfirmDialog>
    <Dialog open={Boolean(conflict)} onClose={saving ? undefined : () => setConflict(null)} fullWidth maxWidth="sm">
      <DialogTitle>같은 속성이 있는 폴더</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mt: 1 }}>
          {conflict?.folderPath || conflict?.folderName}
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          현재 파일: {conflict?.currentFileName || '없음'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
          옮기면 그 폴더의 속성은 빠지고, 파일은 각 폴더에 남습니다.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={() => setConflict(null)} disabled={saving}>취소</Button>
        <Button variant="contained" onClick={handleMoveAttribute} disabled={saving}>옮기기</Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default SubMatFolderSpecDialog;
